import { BodyText, Button, Eyebrow, Stack } from '@servicetitan/design-system';
import styles from './Keypad.module.css';
import keypadKeys from './keypadKeys';
import classNames from 'classnames';
import { mainLayoutLocators } from '@locators/main-layout';

type KeypadVariant = 'callControl' | 'callModal';

const CALL_CONTROL_KEY_WIDTH = 84;
const CALL_MODAL_KEY_WIDTH = 103;

interface KeypadProps {
  variant: KeypadVariant;
  onKeyClick: (key: string) => void;
}

interface KeypadKeyProps {
  keyValue: string;
  keySubtext?: string;
  variant: KeypadVariant;
  onClick: (value: string) => void;
}

const KeypadKey = ({
  keyValue,
  keySubtext,
  onClick,
  variant,
}: KeypadKeyProps) => {
  const isCallControlVariant = variant === 'callControl';

  return (
    <Button
      className={classNames(
        'h-100',
        { 'p-y-half': isCallControlVariant },
        { 'p-y-1': !isCallControlVariant },
        {
          'bg-neutral-400-i': isCallControlVariant,
        },
        { [styles.callControlButton]: isCallControlVariant },
      )}
      outline
      id={`${mainLayoutLocators.outboundAndTransferModal.keypadBtn}-${keyValue}`}
      onClick={() => onClick(keyValue)}
      width={
        variant === 'callControl'
          ? CALL_CONTROL_KEY_WIDTH
          : CALL_MODAL_KEY_WIDTH
      }
    >
      <Stack direction='column' className={styles.callControlContent}>
        <BodyText
          className={classNames({
            'c-neutral-20': isCallControlVariant,
          })}
        >
          {keyValue}
        </BodyText>
        <Eyebrow
          className={classNames(
            { 'c-neutral-90': !isCallControlVariant },
            { 'c-neutral-70': isCallControlVariant },
            'fw-normal',
          )}
        >
          {keySubtext}
        </Eyebrow>
      </Stack>
    </Button>
  );
};

/**
 * Renders a keypad component.
 *
 * @component
 * @param {string} props.variant - The variant of the keypad, either 'callControl' or 'callModal'.
 * @param {Function} props.onKeyClick - The callback function to handle key clicks.
 */
const Keypad = ({ variant, onKeyClick }: KeypadProps) => {
  return (
    <Stack
      direction='column'
      alignItems='center'
      spacing={variant === 'callControl' ? 1 : 2}
    >
      {keypadKeys.map((keyRow) => (
        <Stack
          direction='row'
          key={`row-${keyRow.map((row) => row.value).join('-')}`}
          spacing={variant === 'callControl' ? 1 : 2}
        >
          {keyRow.map((key) => (
            <KeypadKey
              variant={variant}
              keyValue={key.value}
              keySubtext={key.subtext}
              onClick={onKeyClick}
              key={key.value}
            />
          ))}
        </Stack>
      ))}
    </Stack>
  );
};

export default Keypad;
